import React from 'react'
import { Card, Typography } from 'antd'
import { PiTargetBold, PiPauseBold, PiClockClockwiseBold } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { PTOSuggestionsContextInfo } from '@vacationtracker/shared/types/user-insight'
import { PTOSuggestionsResponse } from '../../../types'
import { getPausedMessage, getCheckBackMessage } from '../utils/contextMessages'
import { ContextMessage } from './context-message'

const { Title, Text } = Typography

interface NoSuggestionsStateProps {
  ptoSuggestions: PTOSuggestionsResponse
}

/**
 * Renders the state when there are no PTO suggestions available
 */
export const NoSuggestionsState: React.FC<NoSuggestionsStateProps> = ({ ptoSuggestions }) => {
  const { formatMessage } = useIntl()
  const pausedMessage = getPausedMessage(ptoSuggestions, formatMessage)
  const checkBackMessage = getCheckBackMessage(ptoSuggestions, formatMessage)

  if (pausedMessage) {
    const contextType = ptoSuggestions.contextInfo?.type
    const IconComponent = contextType === PTOSuggestionsContextInfo.UPCOMING_LEAVE ? PiClockClockwiseBold : PiPauseBold

    return (
      <Card className="no-suggestions-card">
        <div className="no-suggestions-content">
          <div className="paused-message-container">
            <div className="paused-icon-wrapper">
              <div className="paused-icon-circle">
                <IconComponent className="paused-icon" />
              </div>
            </div>
            <div className="paused-content">
              <Title level={4} className="paused-title">
                {pausedMessage.title}
              </Title>
              <Text type="secondary" className="paused-description">
                {pausedMessage.description}
              </Text>
            </div>
          </div>
          {checkBackMessage && (
            <div className="paused-checkback-section">
              <div className="paused-separator" />
              <Text type="secondary" className="paused-checkback">
                • {checkBackMessage}
              </Text>
            </div>
          )}
        </div>
      </Card>
    )
  }

  // Fallback to context message if available
  const contextMessage = ptoSuggestions.contextInfo ? <ContextMessage ptoSuggestions={ptoSuggestions} formatMessage={formatMessage} /> : null

  if (contextMessage) {
    return (
      <Card className="no-suggestions-card">
        <div className="no-suggestions-content">
          <div className="human-message">{contextMessage}</div>
        </div>
      </Card>
    )
  }

  // Default fallback
  return (
    <Card className="no-suggestions-card">
      <div className="no-suggestions-content">
        <div className="no-suggestions-fallback">
          <Text type="secondary" className="no-suggestions-title">
            <PiTargetBold /> <IntlMessages id="energyLevel.noSuggestionsTitle" />
          </Text>
          <Text type="secondary" className="no-suggestions-description">
            <IntlMessages id="energyLevel.noSuggestionsDescription" />
          </Text>
        </div>
      </div>
    </Card>
  )
}
